import { useRef, useState } from "react";
import { toast } from "react-toastify";
import { env } from "../../../env";


type UploadStatus = "pending" | "uploading" | "success" | "error";

interface UploadFileItem {
  id: string;
  file: File;
  progress: number;
  status: UploadStatus;
  response?: any;
  error?: string;
}

interface Props {
  directory?: string;
  label?: string;
  docAcept?: string;
  maxSize?: number;
  disabled?: boolean;
  autoUpload?: boolean;
  onCreate?: (uploadedFiles: any[]) => void | Promise<any>;
}

export const MultipleFileInput = ({
  directory,
  label,
  docAcept,
  maxSize,
  disabled,
  autoUpload = false,
  onCreate,
}: Props) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const uploadedFilesRef = useRef<any[]>([]);
  const cancelledFileIdsRef = useRef<Set<string>>(new Set());

  const [files, setFiles] = useState<UploadFileItem[]>([]);
  const [dragActive, setDragActive] = useState(false);

  const baseURL =
    env.REACT_APP_ENDPOINT + env.REACT_APP_DEPLOY + env.REACT_APP_FILE;

  const validateFile = (file: File): boolean => {
    if (maxSize && file.size > maxSize) {
      toast.error(`El archivo ${file.name} supera el tamaño permitido`);
      return false;
    }

    if (docAcept && docAcept.trim() !== "") {
      const acceptedTypes = docAcept.split(",").map((x) => x.trim());

      const valid = acceptedTypes.some((type) => {
        if (type.endsWith("/*")) {
          return file.type.startsWith(type.replace("/*", "/"));
        }
        return file.type === type;
      });

      if (!valid) {
        toast.error(`Formato no válido: ${file.name} - ${file.type}`);
        return false;
      }
    }

    return true;
  };

  const addFiles = (fileList: FileList | File[]) => {
    const selectedFiles = Array.from(fileList)
      .filter(validateFile)
      .map((file) => ({
        id: crypto.randomUUID(),
        file,
        progress: 0,
        status: "pending" as UploadStatus,
      }));

    setFiles((prev) => [...prev, ...selectedFiles]);

    if (autoUpload && selectedFiles.length > 0) {
      void uploadItems(selectedFiles);
    }
  };

  const removeFile = (id: string) => {
    const itemToRemove = files.find((item) => item.id === id);

    if (itemToRemove?.status === "pending") {
      cancelledFileIdsRef.current.add(id);
    }

    if (itemToRemove?.response) {
      uploadedFilesRef.current = uploadedFilesRef.current.filter(
        (uploadedFile) => uploadedFile !== itemToRemove.response
      );
    }

    setFiles((prev) => prev.filter((item) => item.id !== id));
    void onCreate?.([...uploadedFilesRef.current]);
  };

  const uploadFile = (item: UploadFileItem): Promise<any> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const formData = new FormData();

      const data = {
        SessionID: 0,
        Zipped: 0,
        PartIndex: 0,
        DataStore: null,
        SerializerType: "json",
        Filename: item.file.name,
        Directory: directory ?? "docserp",
        Function: "upload",
      };

      formData.append("FileUpload", item.file, JSON.stringify(data));

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded * 100) / event.total);

          setFiles((prev) =>
            prev.map((f) =>
              f.id === item.id
                ? { ...f, progress: percent, status: "uploading" }
                : f
            )
          );
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          let response: any = null;

          try {
            response = JSON.parse(xhr.responseText);
          } catch {
            response = xhr.responseText;
          }

          const uploadedFile =
            response?.DataBeanProperties?.ObjectValue?.DataBeanProperties ??
            response?.DataBeanProperties ??
            response;

          const normalizedFile = {
            ...uploadedFile,
            OriginalName: item.file.name,
          };

          setFiles((prev) =>
            prev.map((f) =>
              f.id === item.id
                ? {
                    ...f,
                    progress: 100,
                    status: "success",
                    response: normalizedFile,
                  }
                : f
            )
          );

          resolve(normalizedFile);
        } else {
          reject(`Error HTTP ${xhr.status}`);
        }
      };

      xhr.onerror = () => {
        reject("Error de conexión");
      };

      xhr.open("POST", baseURL);
      xhr.send(formData);
    });
  };

  const uploadItems = async (pendingFiles: UploadFileItem[]) => {
    const uploadedResponses: any[] = [];
    let hasError = false;

    for (const item of pendingFiles) {
      if (cancelledFileIdsRef.current.has(item.id)) {
        cancelledFileIdsRef.current.delete(item.id);
        continue;
      }

      try {
        const response = await uploadFile(item);
        uploadedResponses.push(response);
        uploadedFilesRef.current = [...uploadedFilesRef.current, response];
        await onCreate?.([...uploadedFilesRef.current]);
      } catch (error: any) {
        hasError = true;
        setFiles((prev) =>
          prev.map((f) =>
            f.id === item.id
              ? {
                  ...f,
                  status: "error",
                  error: String(error),
                }
              : f
          )
        );

        toast.error(`Error al subir ${item.file.name}`);
      }
    }

    if (!hasError && uploadedResponses.length === pendingFiles.length) {
      toast.success("Archivos cargados satisfactoriamente");
    }
  };

  const uploadAll = async () => {
    const pendingFiles = files.filter((f) => f.status === "pending");

    if (pendingFiles.length === 0) {
      toast.info("No hay archivos pendientes por cargar");
      return;
    }

    await uploadItems(pendingFiles);
  };

  const clearAll = () => {
    files.forEach((item) => {
      if (item.status === "pending") {
        cancelledFileIdsRef.current.add(item.id);
      }
    });
    uploadedFilesRef.current = [];
    setFiles([]);
    void onCreate?.([]);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    setDragActive(false);

    if (disabled) return;

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
      e.dataTransfer.clearData();
    }
  };

  return (
    <div>
      <label className="form-label">{label ?? "Adjuntos"}</label>

      <div
        onClick={() => !disabled && inputRef.current?.click()}
        onDragEnter={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setDragActive(false);
        }}
        onDrop={handleDrop}
        className={`border rounded p-4 text-center ${
          dragActive ? "bg-light border-primary" : ""
        } ${disabled ? "opacity-50" : ""}`}
        style={{
          cursor: disabled ? "not-allowed" : "pointer",
          borderStyle: "dashed",
        }}
      >
        <i className="mdi mdi-cloud-upload-outline fs-1" />

        <div>
          <strong>Arrastra archivos aquí</strong>
        </div>

        <div className="text-muted">
          o haz clic para seleccionarlos
        </div>

        <input
          ref={inputRef}
          type="file"
          multiple
          disabled={disabled}
          accept={docAcept ?? ""}
          className="d-none"
          onChange={(e) => {
            if (e.target.files) {
              addFiles(e.target.files);
              e.target.value = "";
            }
          }}
        />
      </div>

      {files.length > 0 && (
        <div className="mt-3">
          {files.map((item) => (
            <div key={item.id} className="border rounded p-2 mb-2">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <strong>{item.file.name}</strong>
                  <div className="text-muted small">
                    {(item.file.size / 1024 / 1024).toFixed(2)} MB
                  </div>
                </div>

                {item.status !== "uploading" && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => removeFile(item.id)}
                  >
                    Quitar
                  </button>
                )}
              </div>

              <div className="progress mt-2" style={{ height: "8px" }}>
                <div
                  className={`progress-bar ${
                    item.status === "success"
                      ? "bg-success"
                      : item.status === "error"
                      ? "bg-danger"
                      : ""
                  }`}
                  style={{ width: `${item.progress}%` }}
                />
              </div>

              <div className="small mt-1">
                {item.status === "pending" && "Pendiente"}
                {item.status === "uploading" && `Cargando ${item.progress}%`}
                {item.status === "success" && "Cargado correctamente"}
                {item.status === "error" && `Error: ${item.error}`}
              </div>
            </div>
          ))}

          {!autoUpload && (
            <button
              type="button"
              className="btn btn-primary mt-2"
              disabled={disabled}
              onClick={uploadAll}
            >
              Subir archivos
            </button>
          )}

          <button
            type="button"
            className="btn btn-outline-secondary mt-2 ms-2"
            disabled={disabled || files.some((item) => item.status === "uploading")}
            onClick={clearAll}
          >
            Limpiar
          </button>
        </div>
      )}
    </div>
  );
};
